/**
 * @license
 * Copyright (c) 2021 Handsoncode. All rights reserved.
 */
import { absoluteSheetReference, invalidSimpleColumnAddress, simpleColumnAddress } from '../Cell';
import { columnIndexToLabel } from './addressRepresentationConverters';
export var ReferenceType;
(function (ReferenceType) {
    ReferenceType["RELATIVE"] = "RELATIVE";
    ReferenceType["ABSOLUTE"] = "ABSOLUTE";
})(ReferenceType || (ReferenceType = {}));
export class ColumnAddress {
    constructor(sheet, col, type) {
        this.sheet = sheet;
        this.col = col;
        this.type = type;
    }
    static absolute(sheet, column) {
        return new ColumnAddress(sheet, column, ReferenceType.ABSOLUTE);
    }
    static relative(sheet, column) {
        return new ColumnAddress(sheet, column, ReferenceType.RELATIVE);
    }
    isColumnAbsolute() {
        return (this.type === ReferenceType.ABSOLUTE);
    }
    isColumnRelative() {
        return (this.type === ReferenceType.RELATIVE);
    }
    isAbsolute() {
        return (this.type === ReferenceType.ABSOLUTE && this.sheet !== null);
    }
    moved(toSheet, toRight, _toBottom) {
        const newSheet = this.sheet === null ? null : toSheet;
        return new ColumnAddress(newSheet, this.col + toRight, this.type);
    }
    shiftedByColumns(numberOfColumns) {
        return new ColumnAddress(this.sheet, this.col + numberOfColumns, this.type);
    }
    toSimpleColumnAddress(baseAddress) {
        const sheet = absoluteSheetReference(this, baseAddress);
        let column = this.col;
        if (this.isColumnRelative()) {
            column = baseAddress.col + this.col;
        }
        return simpleColumnAddress(sheet, column);
    }
    shiftRelativeDimensions(toRight, _toBottom) {
        const col = this.isColumnRelative() ? this.col + toRight : this.col;
        return new ColumnAddress(this.sheet, col, this.type);
    }
    shiftAbsoluteDimensions(toRight, _toBottom) {
        const col = this.isColumnAbsolute() ? this.col + toRight : this.col;
        return new ColumnAddress(this.sheet, col, this.type);
    }
    withAbsoluteSheet(sheet) {
        return new ColumnAddress(sheet, this.col, this.type);
    }
    isInvalid(baseAddress) {
        return this.toSimpleColumnAddress(baseAddress).col < 0;
    }
    hash(withSheet) {
        const sheetPart = withSheet && this.sheet !== null ? `#${this.sheet}` : '';
        switch (this.type) {
            case ReferenceType.RELATIVE: {
                return `${sheetPart}#COLR${this.col}`;
            }
            case ReferenceType.ABSOLUTE: {
                return `${sheetPart}#COLA${this.col}`;
            }
        }
    }
    unparse(baseAddress) {
        const simpleAddress = this.toSimpleColumnAddress(baseAddress);
        if (invalidSimpleColumnAddress(simpleAddress)) {
            return undefined;
        }
        const column = columnIndexToLabel(simpleAddress.col);
        const dollar = this.type === ReferenceType.ABSOLUTE ? '$' : '';
        return `${dollar}${column}`;
    }
    exceedsSheetSizeLimits(maxColumns) {
        return this.col >= maxColumns;
    }
}
